// Mednos Theme (hell/dunkel) — app-uebergreifend, seit 22.09.2026.
//
// Die Wahl steht in einem Cookie auf `.mednos.eu`, damit sie beim Wechsel
// zwischen papers., study. und radiology.mednos.eu erhalten bleibt. Vorher
// hielt jede App sie in ihrem localStorage — der ist je Subdomain getrennt,
// also sah jede App anders aus, je nachdem, wo man zuletzt umgeschaltet
// hatte. Dasselbe Muster wie `mednos_last_app` in app-switcher.js.
//
// localStorage (`theme`) wird weiter mitgeschrieben: lokal (localhost) gibt
// es kein .mednos.eu-Cookie, und das Cookie fehlt auch beim allerersten
// Besuch nach der Umstellung — dann gilt die alte Wahl der App.
//
// Den FRUEHEN Anstrich vor dem ersten Paint macht je App `public/theme-init.js`
// (externe Datei wegen der CSP `script-src 'self'`); die Lesereihenfolge dort
// muss dieselbe bleiben wie in `themeWaehlen` (s. tests/theme-geteilt.test.js
// in Papers).
//
// Radiology hat keinen hellen Modus und bindet dieses Modul nicht ein.

export const THEME_COOKIE = 'mednos_theme';
const LOKAL_KEY = 'theme';
const EIN_JAHR = 31536000;

const gueltig = (t) => (t === 'light' || t === 'dark' ? t : null);

function isProd() {
  return location.hostname === 'mednos.eu' || location.hostname.endsWith('.mednos.eu');
}

/// Liest das Theme aus einer `document.cookie`-Zeile; null, wenn keins da ist.
export function themeAusCookie(zeile) {
  const m = /(?:^|;\s*)mednos_theme=([^;]*)/.exec(zeile || '');
  return m ? gueltig(decodeURIComponent(m[1])) : null;
}

/// Das geteilte Cookie schlaegt den app-eigenen Speicher; ohne beides hell.
export function themeWaehlen({ cookie, lokal }) {
  return gueltig(cookie) || gueltig(lokal) || 'light';
}

/// Die Cookie-Zeile zum Setzen.
export function themeCookie(theme) {
  return `${THEME_COOKIE}=${theme}; Domain=.mednos.eu; Path=/; SameSite=Lax; Max-Age=${EIN_JAHR}`;
}

function lokalLesen() {
  try { return localStorage.getItem(LOKAL_KEY); } catch { return null; }
}

/// Das gerade gueltige Theme.
export function aktuellesTheme() {
  return themeWaehlen({ cookie: themeAusCookie(document.cookie), lokal: lokalLesen() });
}

function anwenden(theme) {
  const html = document.documentElement;
  if (html.dataset.theme === theme) return;
  html.dataset.theme = theme;
  // Wie theme-init.js: der Seitengrund vor dem ersten Paint des CSS.
  html.style.background = theme === 'dark' ? '#000' : '#fff';
  window.dispatchEvent(new CustomEvent('mednos-theme', { detail: { theme } }));
}

/// Setzt das Theme und merkt es sich — in Produktion fuer alle Apps.
export function setzeTheme(theme) {
  const t = gueltig(theme) || 'light';
  anwenden(t);
  try { localStorage.setItem(LOKAL_KEY, t); } catch { /* privat: dann nur Cookie */ }
  if (isProd()) document.cookie = themeCookie(t);
}

/// Einmal je App beim Start. Gleicht das Theme an, wenn man in einem anderen
/// Tab oder einer anderen App umgeschaltet hat und hierher zurueckkommt —
/// auch nach Zurueck-Navigation aus dem bfcache und nach einem Prerender.
export function initThemeSync() {
  const angleichen = () => anwenden(aktuellesTheme());
  angleichen();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') angleichen();
  });
  window.addEventListener('pageshow', angleichen);
  window.addEventListener('focus', angleichen);
}
